import { Stack } from '@mui/material';
import React, { useMemo, useState } from 'react';
import { SearchResult } from 'app/concierge/types';
import { openInNewTab } from 'tools/tools';
import SimpleResultItem from './types/SimpleResultItem';
import ReferenceResultItem from './types/ReferenceResultItem';
import ElementResultItem from './types/ElementResultItem';
import DatasetDataResultItem from './types/DatasetDataResultItem';
import AccessDeniedPopup from 'components/common/popup/AccessDeniedPopup';
import { AccessDeniedTileInfo } from 'app/home/index.functions';
import { useSelector } from 'react-redux';
import { dispatchCustomEvent } from 'tools/events';
import { mergeCustomStyles } from 'app/concierge/tools/mergeCustomStyles';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from './styles';
import { useMiNavigation } from 'hooks/useNavigation';
import useResultItemsData, { findTileInfo, IResultItemsData } from './useResultItemsData';

interface IProps {
    items: SearchResult[];
    useInfoButton?: boolean;
    /** Lookups already resolved by the caller (AdditionalDataSections); absent, the list resolves its own. */
    data?: IResultItemsData;
}

export default function ResultItems({ items, useInfoButton, data }: IProps) {
    const navigate = useMiNavigation();

    const { tilesInfo, referencesData } = useResultItemsData(items, data);
    const [accessDeniedPopupTile, setAccessDeniedPopupTile] = useState<AccessDeniedTileInfo | null>(null);

    const isAppPanel = useSelector((state: any) => state.concierge.isAppPanel) as boolean;
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;
    const isMobile = useSelector((state: any) => state.concierge.isMobile) as boolean;

    const styles = useMemo(() => {
        return mergeCustomStyles(defaultStyles, customStyles?.activeChatMessageResultItem, 'resultItems');
    }, [customStyles]);

    const onItemClick = (result: SearchResult) => {
        if (isAppPanel) {
            dispatchCustomEvent('mi-concierge-panel', {
                action: 'item-click',
                payload: {
                    item: result,
                },
            });
        } else if (isMobile) {
            // todo: for now mobile can't show these types of entity
        } else {
            switch (result.type) {
                case 'portal_page':
                    if (result.url) {
                        openInNewTab(result.url);
                    }

                    break;
                case 'notification_schedule_distribution':
                    navigate(`/notification/burst/${result.id}`);
                    break;
                case 'dataset':
                    navigate(`/dataset/${result.id}`);
                    break;
                case 'dataset_data':
                    if (result.external_link) {
                        openInNewTab(result.external_link);
                    }
                    break;
                case 'document':
                    if (result.external_link) {
                        openInNewTab(result.external_link);
                    } else {
                        navigate(`/content/index/view-document/document/${result.id}`);
                    }
                    break;
            }
        }
    };

    return (
        <>
            <Stack sx={styles.resultItems}>
                {items.map((result, index) => {
                    if (result.type === 'dashboard_element') {
                        const tileInfo = findTileInfo(tilesInfo, result);

                        if (!tileInfo) {
                            return null;
                        }

                        return (
                            <ElementResultItem
                                result={result}
                                key={index}
                                tileInfo={tileInfo}
                                setAccessDeniedPopupTile={setAccessDeniedPopupTile}
                                useInfoButton={useInfoButton}
                            />
                        );
                    }

                    if (result.type === 'external_report_reference') {
                        return <ReferenceResultItem result={result} key={index} referencesData={referencesData} />;
                    }

                    if (result.type === 'dataset_data') {
                        return <DatasetDataResultItem result={result} key={index} />;
                    }

                    return <SimpleResultItem result={result} key={index} onItemClick={onItemClick} />;
                })}
            </Stack>
            {accessDeniedPopupTile && (
                <AccessDeniedPopup
                    accessDeniedTile={accessDeniedPopupTile}
                    setAccessDeniedPopupTile={setAccessDeniedPopupTile}
                />
            )}
        </>
    );
}
